import { Sparkline } from '@/components/Sparkline';
import type { SnapshotListEntry } from '@/lib/snapshots';

const MAX_POINTS = 12;

export function trendPoints(snapshots: SnapshotListEntry[]): number[] {
  // Snapshots arrive newest-first from the API; the sparkline expects
  // oldest → newest. Filter out legacy rows with no recorded percentage,
  // then keep the most-recent MAX_POINTS so old data doesn't compress
  // the visible trend.
  const populated = snapshots.filter(
    (s): s is SnapshotListEntry & { compliancePct: number } =>
      s.compliancePct !== null,
  );
  const recent = populated.slice(0, MAX_POINTS).reverse();
  return recent.map((s) => s.compliancePct);
}

type Props = { snapshots: SnapshotListEntry[] };

export function TrendCard({ snapshots }: Props) {
  const values = trendPoints(snapshots);
  if (values.length < 2) return null;
  const last = values[values.length - 1];
  const first = values[0];
  const delta = last - first;
  const deltaLabel =
    delta === 0
      ? 'No change'
      : `${delta > 0 ? '+' : ''}${delta} pts since ${first}%`;
  return (
    <section
      data-testid="trend-card"
      className="rounded-lg border border-border bg-surface p-7"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-label uppercase tracking-[0.08em] text-text-secondary">
            Compliance trend
          </p>
          <p className="mt-2 font-display text-h3 font-semibold text-text-primary">
            {last}%
          </p>
          <p className="mt-1 text-body-sm text-text-secondary">
            {deltaLabel} · {values.length} snapshots
          </p>
        </div>
        <Sparkline
          values={values}
          width={240}
          height={56}
          className="text-accent"
        />
      </div>
    </section>
  );
}
